import React, { useState, useEffect } from "react";
import LessonReportForm from "../../components/report/LessonReportForm";
import { getRequest } from "../../apiWrapper";
import { toast } from "react-toastify";

const TeacherDashboard = ({ selectedStudents, setSelectedStudents }) => {
  const [loading, setLoading] = useState(true);
  const [studentColors, setStudentColors] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(selectedStudents[0] || null);

  const DEFAULT_COLOR_KEY = "custom1"; // デフォルトのキー

  // 保存後に呼ばれるコールバック
  const handleStudentSave = async ({ studentid: savedId, subject: savedSubject }) => {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Tokyo" }); // YYYY-MM-DD (JST)
    for (const student of selectedStudents) {
      if (student.studentid === savedId) continue;

      // draft が sessionStorage に残っているか
      const raw = sessionStorage.getItem(`lessonReportData_${student.studentid}`);
      if (!raw) continue;
      // 一度でも入力していれば tmp.subject が入っている想定
      const tmp = JSON.parse(raw)[student.studentid] || {};
      const draftSubject = tmp.subject;
      if (!draftSubject) continue;

      // 過去レポート取得 API を叩いて「今日の日付」のレポートがあるかチェック
      const res = await getRequest(
        `lessons/previous?studentid=${student.studentid}&subjects=${draftSubject}&limit=12`
      );
      const reports = (res && res.reports) || [];
      const hasToday = reports.some(r => r.timestamp_jst.split("T")[0] === today);
      if (!hasToday) {
        toast.warn(
          `${student.name} さんの「${draftSubject}」はまだ保存されていません。タブを移動します。`,
          { autoClose: 5000 }
        );
        setSelectedStudent(student);
        break;
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("📡 APIから teacherAssignments と settings を取得開始");

        // 担当生徒割り当て情報の取得
        const assignmentData = await getRequest(`teacherAssignments`);
        // 設定情報の取得（色マッピング）
        const settingsData = await getRequest("settings/default");

        if (assignmentData && settingsData && settingsData.colors) {
          const studentData = assignmentData.students; // { [studentid]: { color, subjects, ... } }
          const colorMappings = settingsData.colors.studentColors || {};

          console.log("✅ APIから取得成功");
          console.log("📌 取得した `students` データ:", studentData);
          console.log("🎨 取得した `colorMappings` データ:", colorMappings);

          let updated = false;
          const newSelectedStudents = selectedStudents.map((s) => {
            const colorKey = (studentData && studentData[s.studentid] && studentData[s.studentid].color) || DEFAULT_COLOR_KEY;
            const newColor = colorMappings[colorKey];
            console.log(`🟢 生徒 ${s.name} (ID: ${s.studentid}) の処理`);
            console.log(`   - 既存の color: ${s.color}`);
            console.log(`   - APIから取得した colorKey: ${colorKey}`);
            console.log(`   - 変換後の color: ${newColor || "❌ マッピングなし"}`);
            if (s.color === newColor) {
              console.log("   ✅ 既に color があるので更新しません");
              return s;
            }
            updated = true;
            return {
              ...s,
              subjects: studentData && studentData[s.studentid] ? studentData[s.studentid].subjects || [] : [],
              colorKey,
              color: newColor || "#cccccc",
            };
          });

          if (updated) {
            console.log("📌 更新された `selectedStudents` データ:", newSelectedStudents);
            setSelectedStudents(newSelectedStudents);
            sessionStorage.setItem("selectedStudents", JSON.stringify(newSelectedStudents));
          }

          // studentColors を設定
          const updatedColors = {};
          newSelectedStudents.forEach((s) => {
            updatedColors[s.studentid] = colorMappings[s.colorKey] || "#cccccc";
          });
          console.log("🎨 更新された `studentColors`:", updatedColors);
          setStudentColors(updatedColors);
        } else {
          console.warn("⚠️ API から teacherAssignments または settings の情報が取得できませんでした");
        }
      } catch (error) {
        console.error("❌ カラー・科目データの取得エラー:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* メインコンテンツ */}
      <div className="flex-1 p-0 pt-[60px] overflow-auto">
        {loading ? (
          <p className="text-gray-500">ログイン情報を取得中...</p>
        ) : loading ? (
          <p className="text-gray-500">担当生徒のデータを取得中...</p>
        ) : selectedStudents.length === 0 ? (
          <p className="text-gray-500">担当生徒がいません。</p>
        ) : (
          <div>
            {selectedStudent && (
              <LessonReportForm
                studentid={selectedStudent.studentid}
                teacherid={sessionStorage.getItem("teacherid")}
                studentName={selectedStudent.name}
                studentColor={studentColors[selectedStudent.studentid] || "#ccc"}
                onSave={handleStudentSave}
              />
            )}
          </div>
        )}
      </div>

      {/* 生徒タブ */}
      <div className="fixed left-0 bottom-0 lg:left-64 lg:w-[calc(100%-16rem)] w-full bg-white pb-6 h-16 pb-0">
        <div className="flex items-center justify-center gap-2 overflow-x-auto no-scrollbar">
          {selectedStudents.map((student) => (
            <button
              key={student.studentid}
              className={`relative flex items-center justify-center w-24 h-12 px-2 text-xs mb-6 font-semibold transition-all transform hover:scale-105 ${selectedStudent?.studentid === student.studentid ? "scale-110 font-bold text-black" : "text-gray-700"
                }`}
              style={{
                textShadow: `2px 2px 5px ${studentColors[student.studentid] || "#ccc"}`,
              }}
              onClick={() => setSelectedStudent(student)}
              title={student.name}
            >
              <span className="text-ellipsis overflow-hidden whitespace-nowrap max-w-full">
                {student.name}
              </span>
              {selectedStudent?.studentid === student.studentid && (
                <span className="absolute bottom-0 left-0 w-full h-1" style={{ backgroundColor: studentColors[student.studentid] || "#ccc" }}></span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
